/**
 * The `activation` search param's vocabulary, for every route that carries one.
 *
 * Shared rather than declared per route, and not as a tidiness measure: TanStack
 * registers one search space for the whole router, so `activation` has a single
 * app-wide type and two routes declaring it as two different unions is a compile
 * error at whichever `navigate` writes it. Two routes do declare it - the
 * Outbound tasks screen's plans tab and the internal task plans page - so this is
 * the one place the values can live.
 *
 * What is deliberately NOT here is what any of it defaults to. Which member an
 * absent param means, which members a given screen offers, and which order they
 * appear in are all that screen's own questions, and the two screens answer them
 * differently: the internal page opens unfiltered, the Outbound plans tab opens
 * on the plans still awaiting release. Each keeps its own default, chip list and
 * validator; only the vocabulary is common.
 *
 * The values are the URL's tokens and, on both screens, the i18n key of the
 * label - so they are a contract with links already in circulation and are not
 * renamed to match a label that changed.
 */

/**
 * Where a task plan is, as a filter over a plan listing.
 *
 * More than a boolean, because "has this been activated" is not one question: a
 * plan can be waiting on a manager, out on the floor, finished, or in one of the
 * two states that are neither (mid-activation, or activation failed) and are
 * visible only unfiltered.
 *
 * Two vocabularies live here rather than one, because the listing has two params
 * and they answer different questions. `NOT_ACTIVATED`/`ACTIVATED` are
 * `activationState`, the *activation job*'s history. `NOT_STARTED`/`IN_PROGRESS`/
 * `COMPLETED` are that same released activation narrowed by `fulfillmentState`,
 * which is real execution - whether tasks have actually been picked. An
 * activation reaches `completed` the moment the worker has minted the task rows,
 * so `ACTIVATED` is the union of the three execution members and not a fifth
 * state beside them.
 *
 * That union is why both readings are offered rather than only the finer one:
 * `fulfillmentState` takes a single value, so "released, unfinished" is not a
 * request anyone can make - a screen either offers the execution members
 * separately or asks for the whole release with `ACTIVATED`. The Outbound plans
 * tab does the former, the internal plan inventory the latter.
 *
 * How each one becomes a request is the consuming screen's business - see
 * `activationQuery` on either.
 */
export enum ActivationFilter {
  /** No activation row at all: nobody has ever released this plan. */
  NOT_ACTIVATED = "not-activated",
  /** Released, whatever the floor has done with it since. */
  ACTIVATED = "activated",
  /** Released, and no task in it has been claimed yet. */
  NOT_STARTED = "not-started",
  /** Released, claimed, and not finished. */
  IN_PROGRESS = "in-progress",
  /** Released, and every task in it is done. */
  COMPLETED = "completed",
  /** No narrowing - the only view a mid-activation or failed plan appears in. */
  ALL = "all",
}
